import { useCallback } from 'react';
import {
  getProject,
  useBuildingMetadata,
  useUpdateProject,
} from '@client/store/project/project.hook';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { Project } from '@shared/models/project.model';
import {
  getBuildingLifetime,
  updateProjectMetadata,
} from '@shared/helpers/project.helpers';
import { getEnabledScopeIds, withScopeEnabled } from './saved-scopes.helpers';

/** Project-specific choices of which presets are offered in the selector. */
export const useProjectScopes = () => {
  const meta = useBuildingMetadata();
  const updateProject = useUpdateProject();
  const readonly = useIsReadonly();
  /**
   * @param calculationPeriodYears - Period of the scope that replaces a disabled
   * active scope. Saved in the same snapshot so a 422 rebase keeps both fields.
   */
  const setEnabled = useCallback(
    (id: string, enabled: boolean, calculationPeriodYears?: number) => {
      if (readonly) return;
      const current = getProject();
      const periodChanged =
        calculationPeriodYears !== undefined &&
        calculationPeriodYears !== getBuildingLifetime(current);
      const apply = (base: Project) =>
        withScopeEnabled(
          periodChanged
            ? updateProjectMetadata(base, {
                building_lifetime: calculationPeriodYears,
              })
            : base,
          id,
          enabled,
        );
      const project = apply(current);
      if (project === current) return;
      // These are selector visibility preferences, excluded from activity history.
      void updateProject({
        kind: 'snapshot',
        project,
        // The period affects results; visibility alone does not.
        skipEnrichment: !periodChanged,
        rebaseOnRace: apply,
      });
    },
    [readonly, updateProject],
  );
  return { enabledIds: getEnabledScopeIds(meta), setEnabled, readonly };
};
